import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { collection, doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../firebase';

const SolplaceContext = createContext();

const initialState = {
  wallet: null,
  selectedColor: '#FF0000',
  dailyPixels: 0,
  maxDailyPixels: 10,
  canvasData: new Map(),
  zoom: 1,
  panX: 0,
  panY: 0,
  selectedPixel: null,
  stats: {
    totalPixels: 0,
    myPixels: 0,
    activeUsers: 1
  },
  loading: false,
  notification: null
};

const solplaceReducer = (state, action) => {
  switch (action.type) {
    case 'SET_WALLET':
      return { ...state, wallet: action.payload };
    case 'SET_SELECTED_COLOR':
      return { ...state, selectedColor: action.payload };
    case 'SET_DAILY_PIXELS':
      return { ...state, dailyPixels: action.payload };
    case 'SET_CANVAS_DATA':
      return { ...state, canvasData: action.payload };
    case 'ADD_PIXEL':
      const newCanvasData = new Map(state.canvasData);
      newCanvasData.set(action.payload.key, action.payload.pixel);
      return { ...state, canvasData: newCanvasData };
    case 'SET_ZOOM':
      return { ...state, zoom: action.payload };
    case 'SET_SELECTED_PIXEL':
      return { ...state, selectedPixel: action.payload };
    case 'UPDATE_STATS':
      return { ...state, stats: { ...state.stats, ...action.payload } };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_NOTIFICATION':
      return { ...state, notification: action.payload };
    case 'RESET_DAILY_PIXELS':
      return { ...state, dailyPixels: 0 };
    default:
      return state;
  }
};

export const SolplaceProvider = ({ children }) => {
  const [state, dispatch] = useReducer(solplaceReducer, initialState);

  // Available colors (r/place style)
  const colors = [
    '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF',
    '#800000', '#008000', '#000080', '#808000', '#800080', '#008080',
    '#FFA500', '#FFC0CB', '#A52A2A', '#808080', '#000000', '#FFFFFF',
    '#FF1493', '#00FA9A', '#1E90FF', '#FFD700', '#DC143C', '#32CD32'
  ];

  // Suscripción en tiempo real a Firestore para los píxeles
  useEffect(() => {
    dispatch({ type: 'SET_LOADING', payload: true });

    const pixelsRef = collection(db, 'pixels');
    const unsubscribe = onSnapshot(
      pixelsRef,
      (snapshot) => {
        const canvasData = new Map();
        const owners = new Set();

        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          const ts = data.timestamp?.toMillis ? data.timestamp.toMillis() : data.timestamp || Date.now();
          const pixel = {
            color: data.color,
            owner: data.owner,
            timestamp: ts
          };
          canvasData.set(docSnap.id, pixel);
          if (data.owner) {
            owners.add(data.owner);
          }
        });

        dispatch({ type: 'SET_CANVAS_DATA', payload: canvasData });
        dispatch({ 
          type: 'UPDATE_STATS', 
          payload: { 
            totalPixels: canvasData.size, 
            activeUsers: Math.max(owners.size, 1) 
          } 
        });
        dispatch({ type: 'SET_LOADING', payload: false });
      },
      (error) => {
        console.error('Error cargando píxeles de Firebase', error);
        dispatch({ type: 'SET_NOTIFICATION', payload: { 
          message: 'No se pudo cargar el mapa (Firebase)', 
          type: 'error' 
        }});
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    );

    return () => unsubscribe();
  }, []);

  // Load user data when wallet changes
  useEffect(() => {
    if (state.wallet) {
      loadUserData();
    }
  }, [state.wallet]);

  // Check if it's a new day and reset daily pixels
  useEffect(() => {
    const checkNewDay = () => {
      if (state.wallet) {
        const savedData = localStorage.getItem(`solplace_user_${state.wallet}`);
        if (savedData) {
          const userData = JSON.parse(savedData);
          const lastDate = userData.lastPixelDate;
          const today = new Date().toDateString();
          if (lastDate !== today) {
            dispatch({ type: 'RESET_DAILY_PIXELS' });
          }
        }
      }
    };

    checkNewDay();
    const interval = setInterval(checkNewDay, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [state.wallet]);

  // Show welcome message
  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch({ type: 'SET_NOTIFICATION', payload: { 
        message: 'Welcome to Solplace! Connect your wallet to start drawing on the China map.', 
        type: 'success' 
      }});
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const loadUserData = () => {
    const savedData = localStorage.getItem(`solplace_user_${state.wallet}`);
    if (savedData) {
      const userData = JSON.parse(savedData);
      dispatch({ type: 'SET_DAILY_PIXELS', payload: userData.dailyPixels || 0 });
      dispatch({ type: 'UPDATE_STATS', payload: { myPixels: userData.totalPixels || 0 } });
    }
  };

  const saveUserData = () => {
    if (state.wallet) {
      const userData = {
        dailyPixels: state.dailyPixels,
        totalPixels: state.stats.myPixels,
        lastPixelDate: new Date().toDateString()
      };
      localStorage.setItem(`solplace_user_${state.wallet}`, JSON.stringify(userData));
    }
  };

  const placePixel = async (x, y) => {
    if (!state.wallet || state.dailyPixels >= state.maxDailyPixels) {
      return false;
    }

    const pixelKey = `${x},${y}`;
    const existingPixel = state.canvasData.get(pixelKey);

    const newPixel = {
      x,
      y,
      color: state.selectedColor,
      owner: state.wallet,
      timestamp: Date.now()
    };

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      await setDoc(doc(db, 'pixels', pixelKey), newPixel);

      // Optimistic UI update
      dispatch({ type: 'ADD_PIXEL', payload: { key: pixelKey, pixel: newPixel } });
      dispatch({ type: 'SET_DAILY_PIXELS', payload: state.dailyPixels + 1 });

      if (!existingPixel) {
        dispatch({ type: 'UPDATE_STATS', payload: { totalPixels: state.stats.totalPixels + 1 } });
      }
      if (!existingPixel || existingPixel.owner !== state.wallet) {
        dispatch({ type: 'UPDATE_STATS', payload: { myPixels: state.stats.myPixels + 1 } });
      }

      saveUserData();
      return true;
    } catch (error) {
      console.error('Error saving pixel to Firebase', error);
      dispatch({ type: 'SET_NOTIFICATION', payload: { 
        message: 'Could not save the pixel to Firebase. Check Firestore rules or auth.', 
        type: 'error' 
      }});
      return false;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const connectWallet = async () => {
    if (typeof window.ethereum !== 'undefined') {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
        dispatch({ type: 'SET_WALLET', payload: accounts[0] });
        
        // Simulate fetching user data
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        dispatch({ type: 'SET_NOTIFICATION', payload: { message: 'Wallet connected successfully! Ready to place pixels.', type: 'success' } });
      } catch (error) {
        dispatch({ type: 'SET_NOTIFICATION', payload: { message: 'Failed to connect wallet: ' + error.message, type: 'error' } });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    } else {
      dispatch({ type: 'SET_NOTIFICATION', payload: { message: 'Please install MetaMask or another compatible wallet', type: 'warning' } });
    }
  };

  const showNotification = (message, type = 'success') => {
    dispatch({ type: 'SET_NOTIFICATION', payload: { message, type } });
  };

  const value = {
    ...state,
    colors,
    dispatch,
    placePixel,
    connectWallet,
    showNotification,
    saveUserData
  };

  return (
    <SolplaceContext.Provider value={value}>
      {children}
    </SolplaceContext.Provider>
  );
};

export const useSolplace = () => {
  const context = useContext(SolplaceContext);
  if (!context) {
    throw new Error('useSolplace must be used within a SolplaceProvider');
  }
  return context;
};
